import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { PillarCard } from "@/components/calv/PillarCard";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { Callout } from "@/components/calv/Callout";
import { StatsBlock } from "@/components/calv/StatsBlock";
import { DeltaHero } from "@/components/calv/DeltaHero";
import { LogoReferences } from "@/components/calv/LogoReferences";

export const Route = createFileRoute("/")({
  head: () => {
    const h = pageHead("home", "de");
    return { meta: h.meta, links: h.links };
  },
  component: HomePage,
});

const proofPoints = [
  {
    k: "Mandate-Bindung",
    v: "Mandanten seit 5+ Jahren",
    b: "Die meisten unserer Mandate laufen seit über fünf Jahren. Das ist die wirtschaftlich aussagekräftigste Kennzahl in einer Branche, in der Agentur-Wechsel oft jährlich passieren.",
  },
  {
    k: "Im Markt seit",
    v: "2013",
    b: "Über zehn Jahre operative Erfahrung in Performance Marketing — vor PMax, vor iOS 14.5, vor AI-getriebenen Plattformen. Wir kennen die Brüche der letzten Jahre, weil wir sie miterlebt haben.",
  },
  {
    k: "Kunden-Profil",
    v: "rund 80 % E-Commerce",
    b: "Wir arbeiten mit B2C- und B2B-Unternehmen aus unterschiedlichen Branchen — mit klarem Schwerpunkt im E-Commerce, ergänzt um SaaS-Kunden.",
  },
];

const principles = [
  {
    n: "01",
    t: "System statt Disziplinen",
    b: "Wir arbeiten nicht in Silos. SEO, SEA, CRO und Tracking laufen über ein gemeinsames Datenmodell — jede Maßnahme kennt die anderen.",
  },
  {
    n: "02",
    t: "Daten zuerst, dann Empfehlung",
    b: "Kein Kundenprojekt startet ohne Audit. Wenn die Datenlage nicht steht, ist jede Strategie geraten — wir sagen das auch ehrlich.",
  },
  {
    n: "03",
    t: "Operativ durch den Geschäftsführer",
    b: "Kein Hand-off an Juniors nach dem Pitch. André Freier sitzt in jedem Kundenprojekt selbst am Konto — als Default, nicht als Premium-Option.",
  },
  {
    n: "04",
    t: "Vergütung unabhängig vom Budget",
    b: "Wir verdienen nicht mehr, wenn Sie mehr ausgeben. Das hält den Anreiz dort, wo er hingehört — bei Ergebnis, nicht bei Mediavolumen.",
  },
];

function HomePage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow="Online-Performance · seit 2013"
          compactMobile
          headline={
            <>
              Online-Marketing,<br />
              <em>wirtschaftlich gedacht.</em>
            </>
          }
          lead="Online-Marketing funktioniert nur dann, wenn es wirtschaftlich gedacht wird. Wir betreiben SEO, SEA, CRO, Tracking und KI als integriertes System — operativ betreut durch den Geschäftsführer, mit messbarer Wirkung statt Reporting-Kosmetik."
          metaStrip={[
            "ROAS- und KUR-Ziele erreicht oder übertroffen",
            "10–200 k€ monatliche Ad-Budgets",
            "~80 % E-Commerce-Mandate",
          ]}
          secondaryMeta={["Bundesweit · DACH-weit"]}
          primaryCta={{
            eyebrow: "Kostenfrei · circa 30 Minuten",
            label: "Strategiegespräch anfragen",
            to: "/kontakt",
            search: { betreff: "strategiegespraech" },
          }}
          secondaryCta={{
            label: "Oder: methodische Werkzeuge ohne Anmeldung testen",
            to: "/tools",
          }}
        />



        <Section
          number="02"
          kicker="In Zahlen"
          headline={<>Keine inszenierten Cases.<br />Vier Kennziffern aus dem Tagesgeschäft.</>}
          caption="Bandbreiten aus laufenden Mandaten, keine Best-Case-Showcases."
        >
          <RevealOnScroll>
            <StatsBlock />
          </RevealOnScroll>
          <Callout>
            Mehr Budget ist keine Strategie. Kontrolle ist es.
          </Callout>
        </Section>

        <Section
          number="03"
          variant="tinted"
          kicker="Was wir tun"
          headline={<>Leistungen. <em>Auf gemeinsamer Datenbasis.</em></>}
          caption="SEO, Performance Marketing, Conversion-Optimierung, Web-Entwicklung und Tracking greifen auf dieselbe Datenbasis zu. So bleibt sichtbar, welche Maßnahmen wirtschaftlich wirken."
        >
          <div className="grid gap-4 md:grid-cols-3">
            <PillarCard
              cta="Mehr erfahren ↗"
              pillar="SEO & AI SEO"
              discipline="SEO & AI SEO"
              description="Organische Reichweite, die in Google ebenso funktioniert wie in ChatGPT, Perplexity und Claude. Nachhaltig, kompoundierend."
              href="/leistungen/seo-ai-seo"
            />
            <PillarCard
              cta="Mehr erfahren ↗"
              pillar="Performance Marketing"
              discipline="Performance Marketing"
              description="Search-, Social-Media- und Affiliate-Performance über Google Ads, Meta, TikTok, LinkedIn und Microsoft Ads — kontrolliert, datengeführt, ohne Black-Box-Versprechen."
              href="/leistungen/performance-marketing"
            />
            <PillarCard
              cta="Mehr erfahren ↗"
              pillar="Conversion & Web"
              discipline="CRO & Web"
              description="Conversion- und Web-Optimierung als zusammenhängendes Gewerk. Hypothesen-getrieben, architektonisch gedacht."
              href="/leistungen/conversion-web-optimierung"
            />
          </div>
        </Section>

        <Section
          number="04"
          kicker="Eigene Software"
          headline={<>Wir nutzen <em>kein Standard-Tooling.</em> Wir bauen die Werkzeuge selbst.</>}
          caption="Drei eigene Tools sind bei Calvarius produktiv im Einsatz: Delta für Feed-Management, picazzoo für KI-Creatives, GonzalAds für Meta-Kampagnen-Steuerung. Plus die Fähigkeit, individuelle SaaS-Bausteine in einem Bruchteil der bisherigen Zeit zu bauen."
        >
          <RevealOnScroll>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/leistungen/tools" className="inline-flex items-center gap-2 rounded-md bg-lime px-6 py-3 font-mono text-sm font-medium uppercase tracking-wider text-ink transition-colors hover:bg-lime/90">
                Tools im Detail →
              </Link>
              <Link to="/leistungen/delta" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">Delta</Link>
              <span className="font-mono text-xs text-ink-4">·</span>
              <Link to="/leistungen/picazzoo" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">picazzoo</Link>
              <span className="font-mono text-xs text-ink-4">·</span>
              <Link to="/leistungen/gonzalads" className="font-mono text-xs uppercase tracking-wider text-cyan-deep hover:underline">GonzalAds</Link>
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="05"
          variant="deep"
          kicker="Proprietäres Tool"
          headline={<>Delta — <em>Produktdaten als Hebel.</em></>}
          caption="Im E-Commerce ist die Performance-Frage fast immer eine Produktdaten-Frage. DELTA ist unser Feed-Management-Layer, der genau dort ansetzt — versioniert, plattformspezifisch, ohne externe Lizenzkosten."
        >
          <DeltaHero />
        </Section>

        <LogoReferences locale="de" number="05" />

        <Section
          number="06"
          kicker="Beweis"
          headline={
            <>
              Drei Zahlen, die unser Tagesgeschäft <em>präzise beschreiben.</em>
            </>
          }
          caption="Keine inszenierten Hero-Cases. Drei Kennziffern, die zeigen, wofür wir stehen — belegbar über die Arbeit mit aktiven Kunden."
        >
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {proofPoints.map((p) => (
                <div key={p.k} className="bg-white p-7 md:p-8">
                  <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-4">
                    {p.k}
                  </div>
                  <div className="mt-4 font-display text-2xl leading-snug text-cyan-deep md:text-3xl">
                    {p.v}
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-ink-3">
                    {p.b}
                  </p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="07"
          variant="tinted"
          kicker="Wie wir denken"
          headline={<>Vier Prinzipien, an denen man uns <em>messen kann.</em></>}
          caption="Keine Werte-Plakate, keine Mission-Statements. Sondern operative Regeln, die jeden Tag den Unterschied machen."
        >
          <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
            {principles.map((p) => (
              <div key={p.n} className="bg-white p-7">
                <div className="font-mono text-xs text-ink-4">{p.n}</div>
                <div className="mt-3 font-display text-xl text-ink">{p.t}</div>
                <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.b}</p>
              </div>
            ))}
          </div>
          <Callout>
            Wir verdienen nicht mehr, wenn Sie mehr ausgeben — und das ist Absicht.
          </Callout>
        </Section>

        <Section
          number="08"
          variant="deep"
          kicker="Engagement"
          headline={<>Lassen Sie uns über Ihre <em>Performance</em> sprechen.</>}
          caption="Wenn Sie wissen möchten, wo Ihr Online-Marketing aktuell Potenzial verschenkt – und wie sich das strukturiert ändern lässt, sprechen wir gerne miteinander."
        >
          <CTABlock
            variant="deep"
            headline={<>Auf Augenhöhe — und ohne Verkaufsdruck.</>}
            body="Schreiben Sie uns kurz, worum es geht. Wir melden uns innerhalb von zwei Werktagen mit einem Vorschlag für ein Erstgespräch oder einer ehrlichen Einschätzung, falls wir nicht die richtige Adresse sind."
            primary={{ label: "Kontakt aufnehmen", to: "/kontakt" }}
            secondary={{ label: "Leistungen ansehen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
